import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {LOCALE_ID, NgModule} from '@angular/core';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';

import {CustomMinValidatorDirective} from '../directives/custom-min-validator.directive';
import {CustomValueValidatorDirective} from '../directives/custom-value-validator.directive';

import {MatIconModule, MatProgressBarModule} from '@angular/material';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonModule} from '@angular/material/button';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatCardModule} from '@angular/material/card';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatTableModule} from '@angular/material/table';
import {MatTabsModule} from '@angular/material/tabs';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatStepperModule} from '@angular/material/stepper';
import {MatMenuModule} from '@angular/material/menu';
import {
  MatNativeDateModule,
  MatPaginatorModule,
  MatProgressSpinnerModule,
  MatSortModule
} from '@angular/material';

import {CourseValidityPipe} from '../pipe/course-validity.pipe';
import {DateStringPipe} from '../pipe/date-string.pipe';

import {LoginService} from '../services/login.service';
import {FileService} from '../services/file.service';

import {HomepageComponent} from './homepage/homepage.component';
import {LoginComponent} from './login/login.component';
import {MenuComponent} from './menu/menu.component';
import {HeaderComponent} from './general-components/header/header.component';

import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import {ErrorStateMatcher, ShowOnDirtyErrorStateMatcher} from '@angular/material/core';
import {UserComponent} from './general-components/user/user.component';

import {OWL_DATE_TIME_LOCALE, OwlDateTimeModule, OwlNativeDateTimeModule} from 'ng-pick-datetime';
import { ContractualDataComponent } from './contractual-data/contractual-data.component';
import { MonthlyActualComponent } from './monthly-actual/monthly-actual.component';
import { ServiceChangesComponent } from './service-changes/service-changes.component';
import { MonthlyChargesComponent } from './monthly-charges/monthly-charges.component';
import { ReportingComponent } from './reporting/reporting.component';
import { SettingsComponent } from './settings/settings.component';
import { TabGroupComponent } from './contractual-data/tab-group/tab-group.component';
import { TabGroupDashboardComponent } from './dashboard/tab-group/tab-group-dashboard.component';
import {MatRadioModule} from '@angular/material/radio';
import { ContractualValuesComponent } from './contractual-data/contractual-values/contractual-values.component';
import { DiscountComponent } from './contractual-data/discount/discount.component';
import { MinimumCommComponent } from './contractual-data/minimum-comm/minimum-comm.component';
import { LocalCustomerComponent } from './settings/local-customer/local-customer.component';
import { ContractComponent } from './settings/contract/contract.component';
import { ArchitectureStructureComponent } from './settings/architecture-structure/architecture-structure.component';
import { CurrencyComponent } from './settings/currency/currency.component';
import { SettingsTabGroupComponent } from './settings/settings-tab-group/settings-tab-group.component';
import { DialogDetailComponent } from './general-components/dialog-detail/dialog-detail.component';
import {MatDialogModule} from '@angular/material/dialog';
import { MasterDataComponent } from './anagrafiche/master-data.component';
import { FilterServiceChangesComponent } from './service-changes/filter-service-changes/filter-service-changes.component';
import { FilterUserComponent } from './users/filter/filter-user.component';
import { FilterLcComponent } from './settings/local-customer/filter-lc/filter-lc.component';
import { FilterArchitectureComponent } from './settings/architecture-structure/filter-architecture/filter-architecture.component';
import { FilterCurrencyComponent } from './settings/currency/filter-currency/filter-currency.component';
import { LocalCustomerAnagrafComponent } from './anagrafiche/local-customer-anagraf/local-customer-anagraf.component';
import { ResourcesUnitsAnagrafComponent } from './anagrafiche/resources-units-anagraf/resources-units-anagraf.component';
import { ContractTypeAnagrafComponent } from './anagrafiche/contract-type/contract-type-anagraf.component';
import { Sl1AnagrafComponent } from './anagrafiche/sl1-anagraf/sl1-anagraf.component';
import { Sl2AnagrafComponent } from './anagrafiche/sl2-anagraf/sl2-anagraf.component';
import { Sl3AnagrafComponent } from './anagrafiche/sl3-anagraf/sl3-anagraf.component';
import { Sl4AnagrafComponent } from './anagrafiche/sl4-anagraf/sl4-anagraf.component';
import { Sl5AnagrafComponent } from './anagrafiche/sl5-anagraf/sl5-anagraf.component';
import { UnitsAnagrafComponent } from './anagrafiche/units-anagraf/units-anagraf.component';
import { RegionAnagrafComponent } from './anagrafiche/region-anagraf/region-anagraf.component';
import { NewRegionAnagrafComponent } from './anagrafiche/region-anagraf/new-region-anagraf/new-region-anagraf.component';
import { NewContractTypeAnagrafComponent } from './anagrafiche/contract-type/new-contract-type-anagraf/new-contract-type-anagraf.component';
import { NewLocalCustomerAnagrafComponent } from './anagrafiche/local-customer-anagraf/new-local-customer-anagraf/new-local-customer-anagraf.component';
import { NewResourcesUnitsAnagrafComponent } from './anagrafiche/resources-units-anagraf/new-resources-units-anagraf/new-resources-units-anagraf.component';
import { NewUnitsAnagrafComponent } from './anagrafiche/units-anagraf/new-units-anagraf/new-units-anagraf.component';
import { NewSl2AnagrafComponent } from './anagrafiche/sl2-anagraf/new-sl2-anagraf/new-sl2-anagraf.component';
import { NewSl5AnagrafComponent } from './anagrafiche/sl5-anagraf/new-sl5-anagraf/new-sl5-anagraf.component';
import { NewSl4AnagrafComponent } from './anagrafiche/sl4-anagraf/new-sl4-anagraf/new-sl4-anagraf.component';
import { NewSl3AnagrafComponent } from './anagrafiche/sl3-anagraf/new-sl3-anagraf/new-sl3-anagraf.component';
import { NewSl1AnagrafComponent } from './anagrafiche/sl1-anagraf/new-sl1-anagraf/new-sl1-anagraf.component';
import { EditRegionAnagrafComponent } from './anagrafiche/region-anagraf/edit-region-anagraf/edit-region-anagraf.component';
import { EditLocalCustomerAnagrafComponent } from './anagrafiche/local-customer-anagraf/edit-local-customer-anagraf/edit-local-customer-anagraf.component';
import { EditSl1AnagrafComponent } from './anagrafiche/sl1-anagraf/edit-sl1-anagraf/edit-sl1-anagraf.component';
import { EditUnitsAnagrafComponent } from './anagrafiche/units-anagraf/edit-units-anagraf/edit-units-anagraf.component';
import { EditResourceAnagrafComponent } from './anagrafiche/resources-units-anagraf/edit-resource-anagraf/edit-resource-anagraf.component';
import { EditContractTypeAnagrafComponent } from './anagrafiche/contract-type/edit-contract-type-anagraf/edit-contract-type-anagraf.component';
import { EditSl2AnagrafComponent } from './anagrafiche/sl2-anagraf/edit-sl2-anagraf/edit-sl2-anagraf.component';
import { EditSl3AnagrafComponent } from './anagrafiche/sl3-anagraf/edit-sl3-anagraf/edit-sl3-anagraf.component';
import { EditSl4AnagrafComponent } from './anagrafiche/sl4-anagraf/edit-sl4-anagraf/edit-sl4-anagraf.component';
import { EditSl5AnagrafComponent } from './anagrafiche/sl5-anagraf/edit-sl5-anagraf/edit-sl5-anagraf.component';
import { EditCurrencyAnagrafComponent } from './anagrafiche/currency-anagraf/edit-currency-anagraf/edit-currency-anagraf.component';
import { NewCurrencyAnagrafComponent } from './anagrafiche/currency-anagraf/new-currency-anagraf/new-currency-anagraf.component';
import { CurrencyAnagrafComponent } from './anagrafiche/currency-anagraf/currency-anagraf.component';
import { FilterContractComponent } from './settings/contract/filter-contract/filter-contract.component';
import { FilterDiscountComponent } from './contractual-data/discount/filter-discount/filter-discount.component';
import { NewLocalCustomerComponent } from './settings/local-customer/new-local-customer/new-local-customer.component';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import { NewContractComponent } from './settings/contract/new-contract/new-contract.component';
import { NewArchitectureStructureComponent } from './settings/architecture-structure/new-architecture-structure/new-architecture-structure.component';
import { NewCurrencyComponent } from './settings/currency/new-currency/new-currency.component';
import { EditLocalCustomerComponent } from './settings/local-customer/edit-local-customer/edit-local-customer.component';
import { EditArchitectureStructureComponent } from './settings/architecture-structure/edit-architecture-structure/edit-architecture-structure.component';
import { EditCurrencySettingComponent } from './settings/currency/edit-currency-setting/edit-currency-setting.component';
import { EditContractComponent } from './settings/contract/edit-contract/edit-contract.component';
import {LoaderService} from './general-services/service-loader/loader.service';
import { EditDiscountComponent } from './contractual-data/discount/edit-discount/edit-discount.component';
import { NewDiscountComponent } from './contractual-data/discount/new-discount/new-discount.component';
import { FilterContractualValuesComponent } from './contractual-data/contractual-values/filter-contractual-values/filter-contractual-values.component';
import { NewContractualValuesComponent } from './contractual-data/contractual-values/new-contractual-values/new-contractual-values.component';
import { LocalCustomerFilterComponent } from './general-components/filter/local-customer-filter/local-customer-filter.component';
import { RegionFilterComponent } from './general-components/filter/region-filter/region-filter.component';
import { ResourceUnitFilterComponent } from './general-components/filter/resource-unit-filter/resource-unit-filter.component';
import { Sl1FilterComponent } from './general-components/filter/sl1-filter/sl1-filter.component';
import { NumAnnoMeseFilterComponent } from './general-components/filter/num-anno-mese-filter/num-anno-mese-filter.component';
import { NumAnnoFilterComponent } from './general-components/filter/num-anno-filter/num-anno-filter.component';
import { Sl2FilterComponent } from './general-components/filter/sl2-filter/sl2-filter.component';
import { Sl3FilterComponent } from './general-components/filter/sl3-filter/sl3-filter.component';
import { Sl4FilterComponent } from './general-components/filter/sl4-filter/sl4-filter.component';
import { Sl5FilterComponent } from './general-components/filter/sl5-filter/sl5-filter.component';
import { UnitsFilterComponent } from './general-components/filter/units-filter/units-filter.component';
import { ChargingMethodologyFilterComponent } from './general-components/filter/charging-methodology-filter/charging-methodology-filter.component';
import { EditContractualValuesComponent } from './contractual-data/contractual-values/edit-contractual-values/edit-contractual-values.component';
import { ContractTypeFilterComponent } from './general-components/filter/contract-type-filter/contract-type-filter.component';
import { CurrencyFilterComponent} from './general-components/filter/currency-filter/currency-filter.component';
import { FilterMinimumCommComponent } from './contractual-data/minimum-comm/filter-minimum-comm/filter-minimum-comm.component';
import { NewMinimumCommComponent } from './contractual-data/minimum-comm/new-minimum-comm/new-minimum-comm.component';
import { EditMinimumCommComponent } from './contractual-data/minimum-comm/edit-minimum-comm/edit-minimum-comm.component';
import { DeltaVolumiTabComponent } from './monthly-actual/delta-volumi-tab/delta-volumi-tab.component';
import { DeltaVolumiComponent } from './monthly-actual/delta-volumi/delta-volumi.component';
import { NewServiceChangesComponent } from './service-changes/new-service-changes/new-service-changes.component';
import { EditServiceChangesComponent } from './service-changes/edit-service-changes/edit-service-changes.component';
import { FilterDeltaVolumiComponent } from './monthly-actual/delta-volumi/filter-delta-volumi/filter-delta-volumi.component';
import { MonthlyChargesTabComponent } from './monthly-charges/monthly-charges-tab/monthly-charges-tab.component';
import { DeltaChargesComponent } from './monthly-charges/delta-charges/delta-charges.component';
import { NewContractualValuesMassiveComponent } from './contractual-data/contractual-values/new-contractual-values-massive/new-contractual-values-massive.component';
import { EditContractualValuesMassiveComponent } from './contractual-data/contractual-values/edit-contractual-values-massive/edit-contractual-values-massive.component';
import { DialogSubmitComponent } from './general-components/dialog-submit/dialog-submit.component';
import { NewDiscountMassiveComponent } from './contractual-data/discount/new-discount-massive/new-discount-massive.component';
import { EditDiscountMassiveComponent } from './contractual-data/discount/edit-discount-massive/edit-discount-massive.component';
import { DialogSubmitDiscountComponent } from './general-components/dialog-submit-discount/dialog-submit-discount.component';
import { ConsuntivoVolumiComponent } from './monthly-actual/consuntivo-volumi/consuntivo-volumi.component';
import { FilterConsuntivoVolumiComponent } from './monthly-actual/consuntivo-volumi/filter-consuntivo-volumi/filter-consuntivo-volumi.component';
import { ConsuntivoChargesComponent } from './monthly-charges/consuntivo-charges/consuntivo-charges.component';
import { FilterConsuntivoChargesComponent } from './monthly-charges/consuntivo-charges/filter-consuntivo-charges/filter-consuntivo-charges.component';
import { UsersComponent } from './users/users.component';
import { UserNameFilterComponent } from './users/filter/user-name-filter/user-name-filter.component';
import { UserSurnameFilterComponent } from './users/filter/user-surname-filter/user-surname-filter.component';
import { UserEmailFilterComponent } from './users/filter/user-email-filter/user-email-filter.component';
import { UserRoleFilterComponent } from './users/filter/user-role-filter/user-role-filter.component';
import { NewUsersComponent } from './users/new-users/new-users.component';
import { EditUsersComponent } from './users/edit-users/edit-users.component';
import { ProviderAnagrafComponent } from './anagrafiche/provider-anagraf/provider-anagraf.component';
import { NewProviderAnagrafComponent } from './anagrafiche/provider-anagraf/new-provider-anagraf/new-provider-anagraf.component';
import { EditProviderAnagrafComponent } from './anagrafiche/provider-anagraf/edit-provider-anagraf/edit-provider-anagraf.component';
import { CalculatedChargesComponent } from './monthly-charges/calculated-charges/calculated-charges.component';
import { FilterDeltaChargesComponent } from './monthly-charges/delta-charges/filter-delta-charges/filter-delta-charges.component';
import { ConsuntivoVolumiScartoComponent } from './monthly-actual/consuntivo-volumi-scarto/consuntivo-volumi-scarto.component';
import { FilterConsuntivoVolumiScartoComponent } from './monthly-actual/consuntivo-volumi-scarto/filter-consuntivo-volumi-scarto/filter-consuntivo-volumi-scarto.component';
import { MaterialFileUploadComponent } from './general-components/material-file-upload/material-file-upload.component';
import { ConsuntivoUploadComponent } from './monthly-actual/consuntivo-upload/consuntivo-upload.component';
import { NewServiceChangesMassiveComponent } from './service-changes/new-service-changes-massive/new-service-changes-massive.component';
import { DialogSubmitServiceChangesComponent } from './service-changes/dialog-submit-service-changes/dialog-submit-service-changes.component';
import { EditServiceChangesMassiveComponent } from './service-changes/edit-service-changes-massive/edit-service-changes-massive.component';
import { ProviderFilterComponent } from './general-components/filter/provider-filter/provider-filter.component';
import { MonthlyChargesUploadComponent } from './monthly-charges/monthly-charges-upload/monthly-charges-upload.component';
import { WasteChargesComponent } from './monthly-charges/waste-charges/waste-charges.component';
import { FilterWasteChargesComponent } from './monthly-charges/waste-charges/filter-waste-charges/filter-waste-charges.component';
import { FilterCalculatedChargesComponent } from './monthly-charges/calculated-charges/filter-calculated-charges/filter-calculated-charges.component';
import { PriceRangeComponent } from './general-components/price-range/price-range.component';
import { DialogPriceRangeComponent } from './general-components/dialog-price-range/dialog-price-range.component';
import {TokenInterceptor} from '../services/auth/TokenInterceptor';
import { ConsolidateFormComponent } from './dashboard/consolidate-form/consolidate-form.component';
import { UserUsernameFilterComponent } from './users/filter/user-username-filter/user-username-filter.component';
import { QuarterFilterComponent } from './general-components/filter/quarter-filter/quarter-filter.component';
import { TypeFilterComponent } from './general-components/filter/type-filter/type-filter.component';
import { DeltaFilterComponent } from './general-components/filter/delta-filter/delta-filter.component';
import { ClaimsFilterComponent } from './general-components/filter/claims-filter/claims-filter.component';
import { RecoveryFilterComponent } from './general-components/filter/recovery-filter/recovery-filter.component';
import { DialogYearMonthComponent } from './general-components/dialog-year-month/dialog-year-month.component';
import { NgxLoadingModule } from 'ngx-loading';
import { ForecastVolumiComponent } from './monthly-actual/forecast-volumi/forecast-volumi.component';
import { FilterForecastVolumiComponent } from './monthly-actual/forecast-volumi/filter-forecast-volumi/filter-forecast-volumi.component';
import { SizeAnagrafComponent } from './anagrafiche/size-anagraf/size-anagraf.component';
import { SiteAnagrafComponent } from './anagrafiche/site-anagraf/site-anagraf.component';
import { SharenameDeviceAnagrafComponent } from './anagrafiche/sharename-device-anagraf/sharename-device-anagraf.component';
import { NewSizeAnagrafComponent } from './anagrafiche/size-anagraf/new-size-anagraf/new-size-anagraf.component';
import { EditSizeAnagrafComponent } from './anagrafiche/size-anagraf/edit-size-anagraf/edit-size-anagraf.component';
import { NewSiteAnagrafComponent } from './anagrafiche/site-anagraf/new-site-anagraf/new-site-anagraf.component';
import { EditSiteAnagrafComponent } from './anagrafiche/site-anagraf/edit-site-anagraf/edit-site-anagraf.component';
import { NewSharenameDeviceAnagrafComponent } from './anagrafiche/sharename-device-anagraf/new-sharename-device-anagraf/new-sharename-device-anagraf.component';
import { EditSharenameDeviceAnagrafComponent } from './anagrafiche/sharename-device-anagraf/edit-sharename-device-anagraf/edit-sharename-device-anagraf.component';
import { ReimbursmentComponent } from './contractual-data/reimbursment/reimbursment.component';
import { FilterReimbursmentComponent } from './contractual-data/reimbursment/filter-reimbursment/filter-reimbursment.component';
import { TerminationFeeComponent } from './contractual-data/termination-fee/termination-fee.component';
import { FilterTerminationFeeComponent } from './contractual-data/termination-fee/filter-termination-fee/filter-termination-fee.component';
import { OrganizationUnitAnagrafComponent } from './anagrafiche/organization-unit-anagraf/organization-unit-anagraf.component';
import { NewOrganizationUnitAnagrafComponent } from './anagrafiche/organization-unit-anagraf/new-organization-unit-anagraf/new-organization-unit-anagraf.component';
import { EditOrganizationUnitAnagrafComponent } from './anagrafiche/organization-unit-anagraf/edit-organization-unit-anagraf/edit-organization-unit-anagraf.component';
import { EditReimbursmentComponent } from './contractual-data/reimbursment/edit-reimbursment/edit-reimbursment.component';
import { EditTerminationFeeComponent } from './contractual-data/termination-fee/edit-termination-fee/edit-termination-fee.component';
import { TransformationComponent } from './contractual-data/transformation/transformation.component';
import { FilterTransformationComponent } from './contractual-data/transformation/filter-transformation/filter-transformation.component';
import {RoleAnagrafComponent} from "./anagrafiche/role-anagraf/role-anagraf.component";
import {RateTypeAnagrafComponent} from "./anagrafiche/rate-type-anagraf/rate-type-anagraf.component";
import {EditRateTypeAnagrafComponent} from "./anagrafiche/rate-type-anagraf/edit-rate-type-anagraf/edit-rate-type-anagraf.component";
import {NewRateTypeAnagrafComponent} from "./anagrafiche/rate-type-anagraf/new-rate-type-anagraf/new-rate-type-anagraf.component";
import {ServiceVariantFilterComponent} from "./general-components/filter/service-variant-filter/service-variant-filter.component";
import {EditRoleAnagrafComponent} from "./anagrafiche/role-anagraf/edit-role-anagraf/edit-role-anagraf.component";
import {NewRoleAnagrafComponent} from "./anagrafiche/role-anagraf/new-role-anagraf/new-role-anagraf.component";
import { FilterRateCardComponent } from './contractual-data/rate-card/filter-rate-card/filter-rate-card.component';
import { RateCardComponent } from './contractual-data/rate-card/rate-card.component';
import { EditTransformationComponent } from './contractual-data/transformation/edit-transformation/edit-transformation.component';
import { ArcRrcComponent } from './contractual-data/arc-rrc/arc-rrc.component';
import { FilterArcRrcComponent } from './contractual-data/arc-rrc/filter-arc-rrc/filter-arc-rrc.component';
import { ServiceVariantAnagrafComponent } from './anagrafiche/service-variant-anagraf/service-variant-anagraf.component';
import { NewServiceVariantAnagrafComponent } from './anagrafiche/service-variant-anagraf/new-service-variant-anagraf/new-service-variant-anagraf.component';
import { EditServiceVariantAnagrafComponent } from './anagrafiche/service-variant-anagraf/edit-service-variant-anagraf/edit-service-variant-anagraf.component';
import { MilestoneNumberFilterComponent } from './general-components/filter/milestone-number-filter/milestone-number-filter.component';
import { EditRateCardComponent } from './contractual-data/rate-card/edit-rate-card/edit-rate-card.component';
import { NewRateCardComponent } from './contractual-data/rate-card/new-rate-card/new-rate-card.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { RoleAnagrafFilterComponent } from './general-components/filter/role-anagraf-filter/role-anagraf-filter.component';
import { RateTypeFilterComponent } from './general-components/filter/rate-type-filter/rate-type-filter.component';
@NgModule({
  declarations: [
    AppComponent,

    CustomMinValidatorDirective,
    CustomValueValidatorDirective,

    HomepageComponent,
    LoginComponent,
    MenuComponent,
    HeaderComponent,

    UserComponent,

    CourseValidityPipe,
    DateStringPipe,

    ContractualDataComponent,

    MonthlyActualComponent,

    ServiceChangesComponent,

    MonthlyChargesComponent,

    ReportingComponent,

    SettingsComponent,

    TabGroupComponent,

    TabGroupDashboardComponent,

    ContractualValuesComponent,

    DiscountComponent,

    MinimumCommComponent,

    LocalCustomerComponent,

    ContractComponent,

    ArchitectureStructureComponent,

    CurrencyComponent,

    SettingsTabGroupComponent,

    DialogDetailComponent,

    MasterDataComponent,

    RegionAnagrafComponent,

    FilterServiceChangesComponent,

    FilterUserComponent,

    FilterLcComponent,

    FilterArchitectureComponent,

    FilterCurrencyComponent,

    NewRegionAnagrafComponent,

    LocalCustomerAnagrafComponent,

    ResourcesUnitsAnagrafComponent,

    ContractTypeAnagrafComponent,

    NewContractTypeAnagrafComponent,

    NewLocalCustomerAnagrafComponent,

    ContractTypeAnagrafComponent,

    Sl3AnagrafComponent,

    Sl4AnagrafComponent,

    Sl5AnagrafComponent,

    UnitsAnagrafComponent,

    Sl1AnagrafComponent,

    Sl2AnagrafComponent,

    RegionAnagrafComponent,

    NewResourcesUnitsAnagrafComponent,

    NewUnitsAnagrafComponent,

    NewSl2AnagrafComponent,

    NewSl5AnagrafComponent,

    NewSl4AnagrafComponent,

    NewSl3AnagrafComponent,

    NewSl1AnagrafComponent,

    EditRegionAnagrafComponent,

    EditLocalCustomerAnagrafComponent,

    EditSl1AnagrafComponent,

    EditUnitsAnagrafComponent,

    EditResourceAnagrafComponent,

    EditUnitsAnagrafComponent,

    EditContractTypeAnagrafComponent,

    EditSl2AnagrafComponent,

    EditSl3AnagrafComponent,

    EditSl4AnagrafComponent,

    EditSl5AnagrafComponent,

    EditCurrencyAnagrafComponent,

    NewCurrencyAnagrafComponent,

    CurrencyAnagrafComponent,

    FilterContractComponent,

    FilterDiscountComponent,

    NewLocalCustomerComponent,

    NewContractComponent,

    NewCurrencyComponent,

    NewContractComponent,

    NewArchitectureStructureComponent,

    EditLocalCustomerComponent,

    EditContractComponent,

    EditArchitectureStructureComponent,

    EditCurrencySettingComponent,

    EditDiscountComponent,

    NewDiscountComponent,

    FilterContractualValuesComponent,

    NewContractualValuesComponent,

    LocalCustomerFilterComponent,

    RegionFilterComponent,

    ResourceUnitFilterComponent,

    Sl1FilterComponent,

    NumAnnoMeseFilterComponent,

    NumAnnoFilterComponent,

    Sl2FilterComponent,

    Sl3FilterComponent,

    Sl4FilterComponent,

    Sl5FilterComponent,

    UnitsFilterComponent,

    ChargingMethodologyFilterComponent,

    ChargingMethodologyFilterComponent,

    EditContractualValuesComponent,

    ContractTypeFilterComponent,

    CurrencyFilterComponent,

    FilterMinimumCommComponent,

    NewMinimumCommComponent,

    EditMinimumCommComponent,

    NewServiceChangesComponent,

    EditServiceChangesComponent,

    EditMinimumCommComponent,

    DeltaVolumiTabComponent,

    DeltaVolumiComponent,

    FilterDeltaVolumiComponent,

    MonthlyChargesTabComponent,

    DeltaChargesComponent,

    NewContractualValuesMassiveComponent,

    EditContractualValuesMassiveComponent,

    NewContractualValuesMassiveComponent,

    DialogSubmitComponent,

    NewDiscountMassiveComponent,

    EditDiscountMassiveComponent,

    DialogSubmitDiscountComponent,

    ConsuntivoVolumiComponent,

    FilterConsuntivoVolumiComponent,

    ConsuntivoChargesComponent,

    FilterConsuntivoChargesComponent,

    UsersComponent,

    UserNameFilterComponent,

    UserSurnameFilterComponent,

    UserEmailFilterComponent,

    UserRoleFilterComponent,

    NewUsersComponent,

    EditUsersComponent,

    ProviderAnagrafComponent,

    NewProviderAnagrafComponent,

    EditProviderAnagrafComponent,

    CalculatedChargesComponent,

    FilterDeltaChargesComponent,

    ConsuntivoVolumiScartoComponent,

    FilterConsuntivoVolumiScartoComponent,

    MaterialFileUploadComponent,

    ConsuntivoUploadComponent,

    NewServiceChangesMassiveComponent,

    DialogSubmitServiceChangesComponent,

    EditServiceChangesMassiveComponent,

    ProviderFilterComponent,

    MonthlyChargesUploadComponent,

    WasteChargesComponent,

    FilterWasteChargesComponent,

    FilterCalculatedChargesComponent,

    PriceRangeComponent,

    DialogPriceRangeComponent,

    ReportingComponent,

    ConsolidateFormComponent,

    UserUsernameFilterComponent,

    QuarterFilterComponent,

    TypeFilterComponent,

    DeltaFilterComponent,

    ClaimsFilterComponent,

    RecoveryFilterComponent,

    DialogYearMonthComponent,

    ForecastVolumiComponent,

    FilterForecastVolumiComponent,

    SizeAnagrafComponent,

    SiteAnagrafComponent,

    SharenameDeviceAnagrafComponent,

    NewSizeAnagrafComponent,

    EditSizeAnagrafComponent,

    NewSiteAnagrafComponent,

    EditSiteAnagrafComponent,

    NewSharenameDeviceAnagrafComponent,

    EditSharenameDeviceAnagrafComponent,

    ReimbursmentComponent,

    FilterReimbursmentComponent,

    TerminationFeeComponent,

    FilterTerminationFeeComponent,

    OrganizationUnitAnagrafComponent,

    NewOrganizationUnitAnagrafComponent,

    EditOrganizationUnitAnagrafComponent,

    EditReimbursmentComponent,

    EditTerminationFeeComponent,

    TransformationComponent,

    FilterTransformationComponent,

    RoleAnagrafComponent,

    RateTypeAnagrafComponent,

    EditRateTypeAnagrafComponent,

    NewRateTypeAnagrafComponent,

    NewRoleAnagrafComponent,

    EditRoleAnagrafComponent,

    FilterTransformationComponent,

    ServiceVariantFilterComponent,

    RateCardComponent,

    FilterRateCardComponent,

    EditTransformationComponent,

    ArcRrcComponent,

    FilterArcRrcComponent,

    ServiceVariantAnagrafComponent,

    NewServiceVariantAnagrafComponent,

    EditServiceVariantAnagrafComponent,

    MilestoneNumberFilterComponent,

    FilterArcRrcComponent,

    EditRateCardComponent,

    NewRateCardComponent,

    DashboardComponent,

    RoleAnagrafFilterComponent,

    RateTypeFilterComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatButtonModule,
    MatDatepickerModule,
    MatSelectModule,
    MatCardModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatStepperModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    MatMenuModule,
    MatRadioModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressBarModule,
    NgxLoadingModule.forRoot({})
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    LoginService,
    FileService,
    LoaderService,
    {provide: OWL_DATE_TIME_LOCALE, useValue: 'it'},
    {provide: LOCALE_ID, useValue: 'it'},
    {provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher}
  ],
  entryComponents: [
    DialogDetailComponent,
    DialogSubmitComponent,
    DialogSubmitDiscountComponent,
    DialogSubmitServiceChangesComponent,
    DialogPriceRangeComponent,
    DialogYearMonthComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

registerLocaleData(localeIt, 'it');
